import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import type { DataGateway } from '@/data/types';
import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import { deleteAttachment, uploadAttachment } from './attachment-service';
import type { Actor } from './org-service';
import { assertCanManageManual } from './store-manual-service';

// إعداد عامل PDF.js للأداء في المتصفح والبيئات المختلفة
if (typeof window !== 'undefined' && pdfjsLib.GlobalWorkerOptions) {
  try {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString();
  } catch {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version || '4.0.0'}/build/pdf.worker.min.mjs`;
  }
}

export const SPREADSHEET_META_PREFIX = 'tf-meta://manual-sheet';

/* ============================================================================
 * أنواع ونماذج محرر الجداول
 * ========================================================================== */

export interface SpreadsheetCell {
  value: string;
  formula?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  color?: string;
  bgColor?: string;
  fontSize?: number;
  fontFamily?: string;
  align?: 'left' | 'center' | 'right';
  valign?: 'top' | 'middle' | 'bottom';
  wrap?: boolean;
  border?: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
}

export interface SpreadsheetMerge {
  s: { r: number; c: number };
  e: { r: number; c: number };
}

export interface SpreadsheetSheet {
  id: string;
  name: string;
  rowCount: number;
  colCount: number;
  colWidths?: Record<number, number>;
  rowHeights?: Record<number, number>;
  cells: Record<string, SpreadsheetCell>; // key is "r:c"
  merges?: SpreadsheetMerge[];
}

export interface SpreadsheetDocument {
  id: string;
  companyId: string;
  title: string;
  description?: string;
  sheets: SpreadsheetSheet[];
  activeSheetIndex: number;
  scope: 'shared' | 'brand';
  brandId: string | null;
  sourceType: 'manual' | 'excel' | 'pdf';
  originalAttachmentId?: string | null;
  originalFileName?: string | null;
  originalFileType?: 'xlsx' | 'pdf' | 'csv' | null;
  authorUserId: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSpreadsheetInput {
  title: string;
  description?: string;
  sheets?: SpreadsheetSheet[];
  scope?: 'shared' | 'brand';
  brandId?: string | null;
  sourceType?: 'manual' | 'excel' | 'pdf';
  originalAttachmentId?: string | null;
  originalFileName?: string | null;
  originalFileType?: 'xlsx' | 'pdf' | 'csv' | null;
}

/* ============================================================================
 * دوال تحويل الإحداثيات والصيغ الرياضية
 * ========================================================================== */

export function colIndexToName(index: number): string {
  let name = '';
  let curr = index;
  while (curr >= 0) {
    name = String.fromCharCode((curr % 26) + 65) + name;
    curr = Math.floor(curr / 26) - 1;
  }
  return name;
}

export function nameToColIndex(name: string): number {
  const clean = name.trim().toUpperCase();
  let index = 0;
  for (let i = 0; i < clean.length; i++) {
    index = index * 26 + (clean.charCodeAt(i) - 64);
  }
  return index - 1;
}

export function cellKey(r: number, c: number): string {
  return `${r}:${c}`;
}

export function parseCellKey(key: string): { r: number; c: number } {
  const [r, c] = key.split(':').map(Number);
  return { r: isNaN(r) ? 0 : r, c: isNaN(c) ? 0 : c };
}

export function coordToA1(r: number, c: number): string {
  return `${colIndexToName(c)}${r + 1}`;
}

export function a1ToCoord(a1: string): { r: number; c: number } | null {
  const match = a1.trim().toUpperCase().match(/^([A-Z]+)(\d+)$/);
  if (!match) return null;
  const col = nameToColIndex(match[1]);
  const row = parseInt(match[2], 10) - 1;
  return { r: row, c: col };
}

export function parseRange(
  rangeStr: string,
): Array<{ r: number; c: number }> {
  const parts = rangeStr.split(':');
  if (parts.length === 1) {
    const coord = a1ToCoord(parts[0]);
    return coord ? [coord] : [];
  }
  if (parts.length === 2) {
    const start = a1ToCoord(parts[0]);
    const end = a1ToCoord(parts[1]);
    if (!start || !end) return [];
    const minR = Math.min(start.r, end.r);
    const maxR = Math.max(start.r, end.r);
    const minC = Math.min(start.c, end.c);
    const maxC = Math.max(start.c, end.c);
    const res: Array<{ r: number; c: number }> = [];
    for (let r = minR; r <= maxR; r++) {
      for (let c = minC; c <= maxC; c++) {
        res.push({ r, c });
      }
    }
    return res;
  }
  return [];
}

/**
 * محرك حساب الصيغ الرياضية (SUM, AVERAGE, COUNT, MIN, MAX والعمليات الحسابية)
 */
export function evaluateFormula(
  formula: string,
  cells: Record<string, SpreadsheetCell>,
  visited: Set<string> = new Set(),
): string {
  const raw = formula.trim();
  if (!raw.startsWith('=')) return raw;

  const expr = raw.slice(1).trim();

  // 1. فحص الدوال SUM, AVERAGE, COUNT, MIN, MAX
  const funcMatch = expr.match(/^([A-Za-z]+)\((.*)\)$/);
  if (funcMatch) {
    const funcName = funcMatch[1].toUpperCase();
    const argsStr = funcMatch[2].trim();

    // تقسيم المعاملات (قد تحتوي على نطاق A1:A5 أو قائمة A1, B2)
    const tokens = argsStr.split(',').map((s) => s.trim()).filter(Boolean);
    const targetCoords: Array<{ r: number; c: number }> = [];

    for (const token of tokens) {
      if (token.includes(':')) {
        targetCoords.push(...parseRange(token));
      } else {
        const coord = a1ToCoord(token);
        if (coord) targetCoords.push(coord);
      }
    }

    const values: number[] = [];
    let filledCount = 0;

    for (const coord of targetCoords) {
      const key = cellKey(coord.r, coord.c);
      const cell = cells[key];
      if (cell) {
        if (cell.value.trim() !== '') filledCount++;
        let valStr = cell.value;
        if (cell.formula) {
          if (visited.has(key)) return '#CIRCULAR!';
          const nextVisited = new Set(visited).add(key);
          valStr = evaluateFormula(cell.formula, cells, nextVisited);
        }
        const num = parseFloat(valStr);
        if (!isNaN(num)) {
          values.push(num);
        }
      }
    }

    switch (funcName) {
      case 'SUM': {
        const sum = values.reduce((acc, v) => acc + v, 0);
        return String(Math.round(sum * 10000) / 10000);
      }
      case 'AVERAGE':
      case 'AVG': {
        if (values.length === 0) return '0';
        const avg = values.reduce((acc, v) => acc + v, 0) / values.length;
        return String(Math.round(avg * 10000) / 10000);
      }
      case 'COUNT': {
        return String(filledCount);
      }
      case 'MIN': {
        if (values.length === 0) return '0';
        return String(Math.min(...values));
      }
      case 'MAX': {
        if (values.length === 0) return '0';
        return String(Math.max(...values));
      }
      default:
        break;
    }
  }

  // 2. فحص العمليات الحسابية البسيطة: مثل =A1+B1 أو =(A1*2)+5
  try {
    const substituted = expr.replace(/[A-Za-z]+[0-9]+/g, (match) => {
      const coord = a1ToCoord(match);
      if (!coord) return '0';
      const key = cellKey(coord.r, coord.c);
      const cell = cells[key];
      if (!cell) return '0';
      let valStr = cell.value;
      if (cell.formula) {
        if (visited.has(key)) throw new Error('CIRCULAR');
        const nextVisited = new Set(visited).add(key);
        valStr = evaluateFormula(cell.formula, cells, nextVisited);
      }
      const num = parseFloat(valStr);
      return isNaN(num) ? '0' : String(num);
    });

    // تحقق من أمان التعبير الرياضي قبل التقييم
    if (!/^[0-9+\-*/().\s]+$/.test(substituted)) {
      return '#VALUE!';
    }

    // حساب آمن بدون eval عام
    const result = Function(`"use strict"; return (${substituted})`)();
    if (typeof result === 'number' && !isNaN(result)) {
      return String(Math.round(result * 10000) / 10000);
    }
    return String(result ?? '');
  } catch {
    return '#ERROR!';
  }
}

/**
 * الحصول على القيمة المعروضة للخلية (مع معالجة الصيغ إذا وُجدت)
 */
export function getCellDisplayValue(
  cell: SpreadsheetCell | undefined,
  cells: Record<string, SpreadsheetCell>,
): string {
  if (!cell) return '';
  if (cell.formula) {
    return evaluateFormula(cell.formula, cells);
  }
  return cell.value;
}

/* ============================================================================
 * ورقة عمل افتراضية جديدة
 * ========================================================================== */

export function createDefaultSheet(
  name = 'ورقة 1',
  rowCount = 25,
  colCount = 10,
): SpreadsheetSheet {
  return {
    id: newId(),
    name,
    rowCount,
    colCount,
    colWidths: {},
    rowHeights: {},
    cells: {},
    merges: [],
  };
}

/* ============================================================================
 * إدارة الجداول في قاعدة البيانات (CRUD)
 * ========================================================================== */

export async function listSpreadsheets(
  gateway: DataGateway,
  companyId: string,
): Promise<SpreadsheetDocument[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
  });

  const docs: SpreadsheetDocument[] = [];
  for (const row of rows) {
    if (
      !row.storagePath.startsWith(`${SPREADSHEET_META_PREFIX}/`) ||
      !row.checksum
    ) {
      continue;
    }
    try {
      const parsed = JSON.parse(row.checksum) as Omit<
        SpreadsheetDocument,
        'id' | 'companyId' | 'createdAt'
      >;
      docs.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
      });
    } catch {
      // تجاهل البيانات التالفة
    }
  }

  return docs.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function getSpreadsheet(
  gateway: DataGateway,
  id: string,
): Promise<SpreadsheetDocument | null> {
  const row = await gateway.table('attachments').get(id).catch(() => null);
  if (
    !row ||
    row.deletedAt ||
    !row.storagePath.startsWith(`${SPREADSHEET_META_PREFIX}/`) ||
    !row.checksum
  ) {
    return null;
  }
  try {
    const parsed = JSON.parse(row.checksum) as Omit<
      SpreadsheetDocument,
      'id' | 'companyId' | 'createdAt'
    >;
    return {
      ...parsed,
      id: row.id,
      companyId: row.companyId,
      createdAt: row.createdAt,
    };
  } catch {
    return null;
  }
}

export async function createSpreadsheet(
  gateway: DataGateway,
  actor: Actor,
  input: CreateSpreadsheetInput,
  authorName: string,
): Promise<SpreadsheetDocument> {
  await assertCanManageManual(gateway, actor);

  const title = input.title.trim();
  if (title.length < 2) {
    throw AppError.validation('عنوان الجدول قصير جدًا');
  }

  const id = newId();
  const now = new Date().toISOString();

  const sheets =
    input.sheets && input.sheets.length > 0
      ? input.sheets
      : [createDefaultSheet()];

  const docData: Omit<SpreadsheetDocument, 'id' | 'companyId' | 'createdAt'> = {
    title,
    description: input.description?.trim() || '',
    sheets,
    activeSheetIndex: 0,
    scope: input.scope ?? 'shared',
    brandId: input.scope === 'brand' ? input.brandId ?? null : null,
    sourceType: input.sourceType ?? 'manual',
    originalAttachmentId: input.originalAttachmentId ?? null,
    originalFileName: input.originalFileName ?? null,
    originalFileType: input.originalFileType ?? null,
    authorUserId: actor.userId,
    authorName,
    updatedAt: now,
  };

  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${SPREADSHEET_META_PREFIX}/${id}`,
    fileName: `sheet-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(docData),
    clientRef: `tf-sheet:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...docData,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
  };
}

export async function updateSpreadsheet(
  gateway: DataGateway,
  actor: Actor,
  id: string,
  patch: Partial<SpreadsheetDocument>,
): Promise<SpreadsheetDocument> {
  await assertCanManageManual(gateway, actor);

  const row = await gateway.table('attachments').get(id);
  if (!row || row.companyId !== actor.companyId) {
    throw AppError.notFound('جدول البيانات غير موجود');
  }

  const existing = JSON.parse(row.checksum || '{}') as SpreadsheetDocument;
  const now = new Date().toISOString();

  const updated: SpreadsheetDocument = {
    ...existing,
    id,
    companyId: row.companyId,
    title: patch.title !== undefined ? patch.title.trim() : existing.title,
    description:
      patch.description !== undefined
        ? patch.description.trim()
        : existing.description,
    sheets: patch.sheets ?? existing.sheets,
    activeSheetIndex: patch.activeSheetIndex ?? existing.activeSheetIndex,
    scope: patch.scope ?? existing.scope,
    brandId:
      patch.scope === 'shared'
        ? null
        : patch.brandId !== undefined
          ? patch.brandId
          : existing.brandId,
    sourceType: patch.sourceType ?? existing.sourceType,
    originalAttachmentId:
      patch.originalAttachmentId !== undefined
        ? patch.originalAttachmentId
        : existing.originalAttachmentId,
    originalFileName:
      patch.originalFileName !== undefined
        ? patch.originalFileName
        : existing.originalFileName,
    originalFileType:
      patch.originalFileType !== undefined
        ? patch.originalFileType
        : existing.originalFileType,
    updatedAt: now,
  };

  await gateway.table('attachments').update(id, {
    checksum: JSON.stringify(updated),
    updatedAt: now,
  });

  return updated;
}

export async function deleteSpreadsheet(
  gateway: DataGateway,
  actor: Actor,
  id: string,
): Promise<void> {
  await assertCanManageManual(gateway, actor);

  const row = await gateway.table('attachments').get(id);
  if (!row || row.companyId !== actor.companyId) {
    throw AppError.notFound('جدول البيانات غير موجود');
  }

  try {
    const existing = JSON.parse(row.checksum || '{}') as SpreadsheetDocument;
    if (existing.originalAttachmentId) {
      await deleteAttachment(
        gateway,
        actor,
        existing.originalAttachmentId,
      ).catch(() => undefined);
    }
  } catch {
    // تجاهل
  }

  const now = new Date().toISOString();
  await gateway.table('attachments').update(id, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

/* ============================================================================
 * محرك استيراد وتصدير ملفات Excel (XLSX, CSV)
 * ========================================================================== */

export async function parseExcelFile(
  file: File,
): Promise<{ sheets: SpreadsheetSheet[]; sheetNames: string[] }> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: 'array',
    cellFormula: true,
    cellStyles: true,
    cellDates: true,
  });

  const parsedSheets: SpreadsheetSheet[] = [];

  for (const sheetName of workbook.SheetNames) {
    const ws = workbook.Sheets[sheetName];
    if (!ws) continue;

    const ref = ws['!ref'];
    let rowCount = 25;
    let colCount = 10;

    if (ref) {
      const decodedRef = XLSX.utils.decode_range(ref);
      rowCount = Math.max(decodedRef.e.r + 5, 20);
      colCount = Math.max(decodedRef.e.c + 5, 8);
    }

    const cells: Record<string, SpreadsheetCell> = {};

    // استخراج الخلايا
    for (const key of Object.keys(ws)) {
      if (key.startsWith('!')) continue;
      const coord = a1ToCoord(key);
      if (!coord) continue;

      const cellObj = ws[key];
      const val =
        cellObj.w !== undefined
          ? String(cellObj.w)
          : cellObj.v !== undefined
            ? String(cellObj.v)
            : '';
      const formula = cellObj.f ? `=${cellObj.f}` : undefined;

      cells[cellKey(coord.r, coord.c)] = {
        value: val,
        formula,
      };
    }

    // استخراج الخلايا المدمجة
    const merges: SpreadsheetMerge[] = [];
    if (ws['!merges']) {
      for (const m of ws['!merges']) {
        merges.push({
          s: { r: m.s.r, c: m.s.c },
          e: { r: m.e.r, c: m.e.c },
        });
      }
    }

    // استخراج عرض الأعمدة
    const colWidths: Record<number, number> = {};
    if (ws['!cols']) {
      ws['!cols'].forEach((col, idx) => {
        if (col && col.wpx) {
          colWidths[idx] = col.wpx;
        } else if (col && col.wch) {
          colWidths[idx] = Math.round(col.wch * 8);
        }
      });
    }

    parsedSheets.push({
      id: newId(),
      name: sheetName,
      rowCount,
      colCount,
      colWidths,
      cells,
      merges,
    });
  }

  return {
    sheets:
      parsedSheets.length > 0 ? parsedSheets : [createDefaultSheet('ورقة 1')],
    sheetNames: workbook.SheetNames,
  };
}

export function exportSpreadsheetToXlsx(
  doc: SpreadsheetDocument,
  activeOnly = false,
): Uint8Array {
  const wb = XLSX.utils.book_new();

  const sheetsToExport = activeOnly
    ? [doc.sheets[doc.activeSheetIndex] || doc.sheets[0]]
    : doc.sheets;

  for (const sheet of sheetsToExport) {
    if (!sheet) continue;
    const ws: XLSX.WorkSheet = {};

    let maxR = 0;
    let maxC = 0;

    for (const [key, cell] of Object.entries(sheet.cells)) {
      const coord = parseCellKey(key);
      if (coord.r > maxR) maxR = coord.r;
      if (coord.c > maxC) maxC = coord.c;

      const a1 = coordToA1(coord.r, coord.c);
      const displayVal = getCellDisplayValue(cell, sheet.cells);
      const numVal = parseFloat(displayVal);

      if (!isNaN(numVal) && String(numVal) === displayVal.trim()) {
        ws[a1] = {
          t: 'n',
          v: numVal,
          f: cell.formula ? cell.formula.slice(1) : undefined,
        };
      } else {
        ws[a1] = {
          t: 's',
          v: displayVal,
          f: cell.formula ? cell.formula.slice(1) : undefined,
        };
      }
    }

    // تعيين نطاق الجدول
    ws['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: Math.max(maxR, 5), c: Math.max(maxC, 5) },
    });

    // دمج الخلايا
    if (sheet.merges && sheet.merges.length > 0) {
      ws['!merges'] = sheet.merges.map((m) => ({
        s: { r: m.s.r, c: m.s.c },
        e: { r: m.e.r, c: m.e.c },
      }));
    }

    // عرض الأعمدة
    if (sheet.colWidths) {
      const cols: XLSX.ColInfo[] = [];
      for (let c = 0; c <= maxC; c++) {
        if (sheet.colWidths[c]) {
          cols.push({ wpx: sheet.colWidths[c] });
        } else {
          cols.push({ wch: 12 });
        }
      }
      ws['!cols'] = cols;
    }

    XLSX.utils.book_append_sheet(wb, ws, sheet.name || 'ورقة');
  }

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Uint8Array(out);
}

export function exportSpreadsheetToCsv(sheet: SpreadsheetSheet): string {
  let maxR = 0;
  let maxC = 0;
  for (const key of Object.keys(sheet.cells)) {
    const { r, c } = parseCellKey(key);
    if (r > maxR) maxR = r;
    if (c > maxC) maxC = c;
  }

  const rows: string[] = [];
  for (let r = 0; r <= maxR; r++) {
    const rowVals: string[] = [];
    for (let c = 0; c <= maxC; c++) {
      const cell = sheet.cells[cellKey(r, c)];
      const val = cell ? getCellDisplayValue(cell, sheet.cells) : '';
      // تأمين علامات الاقتباس والفاصلات
      const escaped = val.includes(',') || val.includes('"') || val.includes('\n')
        ? `"${val.replace(/"/g, '""')}"`
        : val;
      rowVals.push(escaped);
    }
    rows.push(rowVals.join(','));
  }

  return rows.join('\r\n');
}

/* ============================================================================
 * محرك استيراد وتفكيك ملفات PDF
 * ========================================================================== */

export interface PdfExtractedResult {
  sheets: SpreadsheetSheet[];
  rawText: string;
  pageCount: number;
}

export async function extractPdfContent(file: File): Promise<PdfExtractedResult> {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) });
  const pdfDoc = await loadingTask.promise;

  const pageCount = pdfDoc.numPages;
  const sheets: SpreadsheetSheet[] = [];
  let fullRawText = '';

  for (let pageNum = 1; pageNum <= Math.min(pageCount, 15); pageNum++) {
    const page = await pdfDoc.getPage(pageNum);
    const textContent = await page.getTextContent();

    interface TextItemPos {
      text: string;
      x: number;
      y: number;
      width: number;
      height: number;
    }

    const items: TextItemPos[] = [];
    for (const item of textContent.items) {
      if ('str' in item && item.str.trim()) {
        const x = item.transform[4];
        const y = item.transform[5];
        items.push({
          text: item.str,
          x,
          y,
          width: item.width || 0,
          height: item.height || 0,
        });
      }
    }

    // تجميع الكلمات حسب السطور (تسامح 4 وحدات رأسية)
    const rowTolerance = 4;
    const lineBuckets: TextItemPos[][] = [];

    // ترتيب العناصر تنازلياً حسب Y (لأن الصفر في PDF من الأسفل)
    items.sort((a, b) => b.y - a.y);

    for (const item of items) {
      let bucket = lineBuckets.find(
        (b) => Math.abs(b[0].y - item.y) <= rowTolerance,
      );
      if (!bucket) {
        bucket = [];
        lineBuckets.push(bucket);
      }
      bucket.push(item);
    }

    // ترتيب كل سطر أفقياً حسب x (تصاعدياً)
    for (const bucket of lineBuckets) {
      bucket.sort((a, b) => a.x - b.x);
    }

    // استخراج نص الصفحة الخام
    const pageText = lineBuckets
      .map((b) => b.map((i) => i.text).join(' '))
      .join('\n');
    fullRawText += `\n--- صفحة ${pageNum} ---\n` + pageText;

    // بناء شبكة الخلايا للورقة
    const cells: Record<string, SpreadsheetCell> = {};
    let maxColIndex = 0;

    lineBuckets.forEach((bucket, rIdx) => {
      bucket.forEach((item, cIdx) => {
        cells[cellKey(rIdx, cIdx)] = {
          value: item.text,
          wrap: true,
        };
        if (cIdx > maxColIndex) maxColIndex = cIdx;
      });
    });

    sheets.push({
      id: newId(),
      name: `صفحة ${pageNum}`,
      rowCount: Math.max(lineBuckets.length + 5, 20),
      colCount: Math.max(maxColIndex + 3, 6),
      cells,
      colWidths: {},
      rowHeights: {},
      merges: [],
    });
  }

  return {
    sheets: sheets.length > 0 ? sheets : [createDefaultSheet('ورقة مستخرجة')],
    rawText: fullRawText.trim(),
    pageCount,
  };
}

/* ============================================================================
 * دالة مساعدة لرفع ملف أصلي (XLSX أو PDF) وحفظه
 * ========================================================================== */

export async function uploadOriginalDocumentFile(
  gateway: DataGateway,
  actor: Actor,
  file: File,
): Promise<{ id: string; fileName: string; mimeType: string }> {
  const uploaded = await uploadAttachment(gateway, actor, {
    companyId: actor.companyId,
    taskId: null,
    file,
    kind: 'document',
    clientRef: `sheet-orig:${newId()}`,
  });
  return {
    id: uploaded.id,
    fileName: uploaded.fileName,
    mimeType: uploaded.mimeType,
  };
}
